<template>
  <div class="w-full max-w-full p-2 sm:p-4 lg:p-6">
    <!-- Page Title -->
    <div class="font-semibold text-lg sm:text-xl lg:text-2xl mb-3 sm:mb-4 lg:mb-6 text-gray-800">
      <div v-if="props.callFrom === 'Activated'">Parents</div>
      <div v-else-if="props.callFrom === 'For Approval'">Pending Request</div>
      <div v-else-if="props.callFrom === 'Inactive'">Approved Inactive</div>
    </div>

    <!-- Responsive DataTable Container -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <DataTable
        :value="customers1"
        :paginator="true"
        :rows="10"
        dataKey="id"
        :rowHover="true"
        v-model:filters="filters1"
        @filter="onTableFilter"
        filterDisplay="menu"
        :loading="loading1"
        :filters="filters1"
        :globalFilterFields="[
          'fname',
          'lname',
          'gender',
          'grade_level',
          'phone_number',
          'address1',
          'address2',
          'brgy',
        ]"
        showGridlines
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        :rowsPerPageOptions="[5, 10, 25, 100, 1000, 10000]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} parents"
        class="responsive-datatable"
      >
        <template #header>
          <!-- Mobile Header (Simplified Layout) -->
          <div class="block lg:hidden p-3 space-y-3 bg-gray-50 border-b">
            <!-- Search -->
            <div class="w-full">
              <IconField>
                <InputIcon>
                  <i class="pi pi-search text-sm" />
                </InputIcon>
                <InputText
                  v-model="filters1['global'].value"
                  placeholder="Search parents..."
                  class="w-full text-sm"
                />
              </IconField>
            </div>
          </div>

          <!-- Desktop Header (Original Layout) -->
          <div class="hidden lg:flex lg:justify-between lg:items-center p-4 bg-gray-50 border-b">
            <div class="flex-1">
              <IconField>
                <InputIcon>
                  <i class="pi pi-search" />
                </InputIcon>
                <InputText
                  v-model="filters1['global'].value"
                  placeholder="Keyword Search"
                  class="w-80 xl:w-96"
                />
              </IconField>
            </div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-6 text-gray-500">
            <i class="pi pi-users text-3xl mb-3 block text-gray-400"></i>
            <p class="text-base font-medium">No parents found</p>
            <p class="text-sm text-gray-400">Try adjusting your search criteria</p>
          </div>
        </template>

        <template #loading>
          <div class="text-center py-6">
            <i class="pi pi-spin pi-spinner text-2xl text-blue-500 mb-2"></i>
            <p class="text-gray-600">Loading parent data. Please wait.</p>
          </div>
        </template>

        <!-- Name Column - Always visible -->
        <Column
          field="name"
          filterField="fname"
          filter-field="lname"
          header="Name"
          style="min-width: 180px"
          frozen
          class="name-column"
        >
          <template #body="{ data }">
            <div class="text-xs sm:text-sm lg:text-base font-medium text-gray-900 leading-tight">
              <div class="truncate max-w-[120px] sm:max-w-[160px] lg:max-w-none" :title="data.mname ? `${data.lname}, ${data.fname} ${data.mname}` : `${data.lname}, ${data.fname}`">
                {{ data.mname ? `${data.lname}, ${data.fname} ${data.mname}` : `${data.lname}, ${data.fname}` }}
              </div>
              <!-- Mobile-only additional info -->
              <div class="block lg:hidden text-xs text-gray-500 mt-1">
                <div>{{ data.contact_number || 'No phone' }}</div>
                <div class="truncate">{{ data.email || 'No email' }}</div>

            <span class="inline-flex items-center mt-2 px-2 py-1 rounded-full text-xs font-medium"
                  :class="getParentStatusClass()">
              {{ getParentStatus() }}

            </span>

            <div class="block mt-2 lg:hidden ">
              <div class="flex  flex-col gap-1">
                <Button
                  type="button"
                  icon="pi pi-eye"
                  label="View"
                  @click="router.push('/parent/profile/' + data.pid)"
                  severity="primary"
                  size="small"
                  class="w-full text-xs py-1"
                />
                <Button
                  v-if="!activated"
                  type="button"
                  icon="pi pi-check-circle"
                  label="Approve"
                  @click="approveParent(data.pid)"
                  severity="info"
                  size="small"
                  class="w-full text-xs py-1"
                />
              </div>
            </div>
              </div>
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search by name" />
          </template>
        </Column>

        <!-- Status Column - Always visible (for mobile context) -->
        <Column
          header="Status"
          style="min-width: 100px"
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="getParentStatusClass()">
              {{ getParentStatus() }}
            </span>
            <!-- Mobile-only additional info -->
            <div class="block lg:hidden text-xs text-gray-500 mt-1">
              <div class="truncate">{{ data.address_1 || 'No address' }}</div>
            </div>
          </template>
        </Column>

        <!-- Email Column - Hidden on mobile -->
        <Column
          header="Email"
          filterField="email"
          style="min-width: 180px"
          class="hidden lg:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[150px]" :title="data.email">
              {{ data.email || 'N/A' }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search by email" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-times"
              @click="filterCallback()"
              severity="secondary"
            />
          </template>
          <template #filterapply="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-check"
              @click="filterCallback()"
              severity="success"
            />
          </template>
        </Column>

        <!-- Phone Number Column - Hidden on mobile and tablets -->
        <Column
          header="Phone Number"
          filterField="contact_number"
          style="min-width: 140px"
          class="hidden xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600">
              {{ data.contact_number || 'N/A' }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search by phone number" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-times"
              @click="filterCallback()"
              severity="secondary"
            />
          </template>
          <template #filterapply="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-check"
              @click="filterCallback()"
              severity="success"
            />
          </template>
        </Column>

        <!-- Address Column - Hidden on mobile and tablets -->
        <Column
          header="Address"
          filterField="address1"
          filter-field="address_1"
          style="min-width: 160px"
          class="hidden xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[140px]" :title="data.address_1">
              {{ data.address_1 || 'N/A' }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search by address" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-times"
              @click="filterCallback()"
              severity="secondary"
            />
          </template>
          <template #filterapply="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-check"
              @click="filterCallback()"
              severity="success"
            />
          </template>
        </Column>

        <!-- Barangay Column - Hidden on mobile and tablets -->
        <Column
          header="Barangay"
          filterField="barangay"
          style="min-width: 120px"
          class="hidden 2xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[100px]" :title="data.barangay">
              {{ data.barangay || 'N/A' }}
            </div>
          </template>
          <template #filter="{ filterModel }">
            <InputText v-model="filterModel.value" type="text" placeholder="Search by barangay" />
          </template>
          <template #filterclear="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-times"
              @click="filterCallback()"
              severity="secondary"
            />
          </template>
          <template #filterapply="{ filterCallback }">
            <Button
              type="button"
              icon="pi pi-check"
              @click="filterCallback()"
              severity="success"
            />
          </template>
        </Column>

        <!-- Actions Column - Conditionally visible -->
        <Column
          v-if="!inactived"
          header="Actions"
          bodyClass="text-center"
          style="min-width: 120px"
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <!-- Mobile Actions - Stacked -->
            <div class="block lg:hidden">
              <div class="flex flex-col gap-1">
                <Button
                  type="button"
                  icon="pi pi-eye"
                  label="View"
                  @click="router.push('/parent/profile/' + data.pid)"
                  severity="primary"
                  size="small"
                  class="w-full text-xs py-1"
                />
                <Button
                  v-if="!activated"
                  type="button"
                  icon="pi pi-check-circle"
                  label="Approve"
                  @click="approveParent(data.pid)"
                  severity="info"
                  size="small"
                  class="w-full text-xs py-1"
                />
              </div>
            </div>

            <!-- Desktop Actions - Horizontal -->
            <div class="hidden lg:flex justify-center items-center flex-wrap gap-2">
              <Button
                type="button"
                icon="pi pi-eye"
                label="View"
                @click="router.push('/parent/profile/' + data.pid)"
                severity="primary"
                size="small"
              />
              <Button
                v-if="!activated"
                type="button"
                icon="pi pi-check-circle"
                label="Approve"
                @click="approveParent(data.pid)"
                severity="info"
                size="small"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Success Dialog -->
    <Dialog
      v-model:visible="successDialog"
      modal
      header="Success"
      :style="getDialogStyle()"
      class="responsive-dialog"
    >
      <p>Parent has been successfully approved!</p>
      <template #footer>
        <Button label="OK" @click="successDialog = false" autofocus />
      </template>
    </Dialog>

    <!-- Failed Dialog -->
    <Dialog
      v-model:visible="failedDialog"
      modal
      header="Failed"
      :style="getDialogStyle()"
      class="responsive-dialog"
    >
      <p>Failed to approve parent. Please try again.</p>
      <template #footer>
        <Button label="OK" @click="failedDialog = false" autofocus />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import Button from 'primevue/button'
import { onBeforeMount, reactive, ref, watch, watchEffect, computed, defineProps } from 'vue'
import { storeToRefs } from 'pinia'
import * as XLSX from 'xlsx'
import router from '@/router'
import { usedStudentStore } from '@/store/admin/StudentStore'
import { usePermissionStore } from '@/store/permissionsStore'
import { useAuthStore } from '@/auth/auth'
import { useParentStore } from '@/store/parent/ParentStore'
import Dialog from 'primevue/dialog'

const props = defineProps({
  callFrom: {
    type: String,
    required: true,
  },
})

const parentStore = useParentStore()
const successDialog = ref(false)
const failedDialog = ref(false)

const { forApproval, approved, parents } = storeToRefs(parentStore)





onBeforeMount( async () =>{
  loading1.value = true
  await parentStore.fetchForApproval()
await parentStore.fetchApproved()
await parentStore.fetchActivated()
  loading1.value = false

})

// Responsive dialog helper
const getDialogStyle = () => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth < 768) {
      return { width: '95vw', maxHeight: '90vh' }
    }
  }
  return { width: '350px' }
}

// Parent status helper functions
const getParentStatus = () => {
  switch (props.callFrom) {
    case 'Activated':
      return 'Active'
    case 'For Approval':
      return 'Pending'
    case 'Inactive':
      return 'Inactive'
    default:
      return 'Unknown'
  }
}

const getParentStatusClass = () => {
  switch (props.callFrom) {
    case 'Activated':
      return 'bg-green-100 text-green-800'
    case 'For Approval':
      return 'bg-yellow-100 text-yellow-800'
    case 'Inactive':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

async function approveParent(pid) {
  const response = await parentStore.approvedParent(pid)
  if (response === 'success') {
    successDialog.value = true
  } else {
    failedDialog.value = true
  }
}

const authStore = useAuthStore()
const permissionsStore = usePermissionStore()
const showDialog = ref(false)

const display = ref(false)
const visibleTop = ref(false)

function open(id) {
  const selected = students.value.find((student) => student.id === id)
  studentStore.addstud(selected)
  if (selected) {
    formselect.name = selected.name
    formselect.age = selected.age
    formselect.birthdate = selected.birthdate
    formselect.gender = selected.gender
    formselect.avegrade = selected.avegrade
    formselect.status = selected.status
    formselect.pass = selected.pass
    formselect.mother = selected.representative.mother
    formselect.father = selected.representative.father
    formselect.phonenumber = selected.phonenumber
    formselect.grade = selected.grade
    formselect.section = selected.section
  } else {
    alert('Student not found!')
  }
  display.value = true
}

function close() {
  display.value = false
  visibleTop.value = true
}

const statuses = reactive(['Drop', 'Active'])
const formselect = reactive({
  First: '',
  grade: '',
  section: '',
  gender: '',
  mother: '',
  father: '',
  birthdate: '',
  avegrade: '',
  status: '',
  phonenumber: '',
  age: 0,
  pass: false,
})

function getid(id) {
  const selected = students.value.find((student) => student.id === id)
  studentStore.addstud(selected)
  if (selected) {
    studentStore.add
    alert(selected.name)
  } else {
    alert('Student not found!')
  }
}

function alset() {
  alert(customers1.value)
}

const displayedStudents = ref([])
const filters1 = ref(null)
const loading1 = ref(false)
const inactived = ref(false)
const activated = ref(false)

function initFilters1() {
  filters1.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    fname: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    lname: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    mname: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    phone_number: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    grade_level: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    status: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    gender: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    email_address: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address1: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address2: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    barangay: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address_1: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  }
}

function formatDate(value) {
  return value.toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function getSeverity(status) {
  switch (status) {
    case 'Drop':
      return 'danger'
    case 'Active':
      return 'success'
    case 'new':
      return 'info'
    case 'negotiation':
      return 'warn'
    case 'renewal':
      return null
  }
}

const onTableFilter = (event) => {
  displayedStudents.value = event.filteredValue
}


const customers1 = computed(() => {
  if (props.callFrom === 'Inactive') {
    inactived.value = true
    activated.value = false
    return approved.value
  }
  else if (props.callFrom === 'For Approval') {
    inactived.value = false
    activated.value = false
    return forApproval.value
  } else { // "Activated" case by default
    inactived.value = false
    activated.value = true
    return parents.value
  }
})

// Initialize filters when the component mounts or callFrom changes
watch(() => props.callFrom, () => {
  initFilters1()
}, { immediate: true })
</script>

<style scoped>

.form-container {
  min-width: unset; /* Remove fixed min-width for mobile responsiveness */
  margin: 0 auto;
  padding: 1rem; /* Add padding for better spacing on all screens */
  max-width: 1200px; /* Optional: Constrain max width on large screens */
}

/* Custom styles for DataTable responsiveness on mobile */
/* These styles will kick in for smaller screens to make the table columns stack */
@media screen and (max-width: 960px) {
  .p-datatable.p-datatable-responsive-mobile .p-datatable-thead > tr > th,
  .p-datatable.p-datatable-responsive-mobile .p-datatable-tfoot > tr > td {
    display: none !important; /* Hide original table headers/footers */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td {
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between; /* Space out label and value */
    width: 100%;
    padding: 0.5rem 1rem;
    border: 0 none;
    border-top: 1px solid var(--surface-d); /* Separator between "rows" of data */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td:last-child {
    border-bottom: 1px solid var(--surface-d); /* Bottom border for the last cell in a stacked row */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td:first-child {
    background: var(--surface-b); /* Highlight the first item (e.g., Name) */
    font-weight: 600;
  }

  /* Label for each row data - uses the 'data-p-field' attribute from PrimeVue's internal structure */
  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td::before {
    content: attr(data-p-field) ":"; /* Displays the column header as a label */
    font-weight: 600;
    margin-right: 0.5rem;
    display: inline-block;
  }
}

/* General layout adjustments for smaller screens */
@media (max-width: 768px) {
  /* Ensure header elements stack nicely */
  .p-datatable-header .flex {
    flex-direction: column;
    align-items: flex-start; /* Align stacked items to the start */
  }

  /* Make sure individual inputs and buttons within header take full width on mobile */
  .p-datatable-header .p-inputtext,
  .p-datatable-header .p-multiselect, /* (if MultiSelect was here) */
  .p-datatable-header .p-button {
    width: 100%;
  }

  /* Adjust gaps for stacked elements */
  .p-datatable-header .flex-column > .flex-column {
    gap: 0.75rem; /* Smaller gap for stacked inner groups */
  }
}
</style>
